import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import {
  ConsentChannel,
  ConsentEvent,
  ConsentPurpose,
  ConsentState,
  CookiePreferences,
  DocumentAcceptance,
  LegalDocumentType,
  PendingDocument,
} from 'src/app/interfaces';
import { AuthService } from './auth.service';
import { HttpService } from './http.service';
import { LocalStorageService } from './local-storage.service';
import { TrackingLoaderService } from './tracking-loader.service';

/**
 * Where the browser-scoped cookie choice lives. Stored `permanent` so it survives `clear()` on
 * logout, and in localStorage so it survives the full-page redirects to the payment providers.
 */
const COOKIE_PREFERENCES_KEY = 'reiteCookieConsent';
/** Bump to re-ask everyone, e.g. when a new category is introduced. */
const COOKIE_PREFERENCES_VERSION = 1;

/**
 * Prefix for "this browser has already been told about version X of document Y". Browser-scoped
 * and `permanent`: the notice is informational, and re-showing it on every sign-in would be noise.
 */
const UPDATE_NOTICE_KEY_PREFIX = 'reiteLegalUpdateSeen-';

interface StoredCookiePreferences extends CookiePreferences {
  version: number;
  at: number;
}

/** A published version the user has not seen, but is not required to accept. */
export interface EditorialUpdate {
  documentType: LegalDocumentType;
  version: string;
}

/** Nothing optional runs until the user says so. */
const DENY_ALL: CookiePreferences = { analytics: false, marketing: false };
const ALLOW_ALL: CookiePreferences = { analytics: true, marketing: true };

/**
 * Single source of truth for what the user has agreed to.
 *
 * Route guards must not fetch domain data, so this service resolves consent once after login and
 * exposes it as signals the guard can read synchronously.
 *
 * **Fails open.** If `/consents/me` cannot be reached, `pendingDocuments` stays empty and nobody is
 * gated. A backend blip must never lock a user out of the app.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  private readonly http = inject(HttpService);
  private readonly localStorage = inject(LocalStorageService);
  private readonly tracking = inject(TrackingLoaderService);
  private readonly auth = inject(AuthService);

  private readonly state = signal<ConsentState | null>(null);
  private readonly cookiePreferencesState = signal<CookiePreferences | null>(
    this.readStoredPreferences()
  );
  /** The uid the current state belongs to, so signing in as someone else re-resolves. */
  private loadedForUid: string | null = null;
  private loaded = false;
  /**
   * When the last `/consents/me` read failed. A failed read is not loaded, so without this bound
   * the retry would put a failing request in front of every route change while the backend is down.
   */
  private lastLoadFailureAt: number | null = null;
  private static readonly RETRY_AFTER_FAILURE_MS = 30_000;
  /** The read currently in flight, so concurrent callers share one request. See `load`. */
  private inFlight: Promise<void> | null = null;

  /** Documents the user must accept before continuing. Empty until proven otherwise. */
  public readonly pendingDocuments = computed<PendingDocument[]>(
    () => this.state()?.pendingDocuments ?? []
  );

  public readonly hasPendingDocuments = computed(() => this.pendingDocuments().length > 0);

  public readonly consentState = this.state.asReadonly();

  /** The stored cookie choice, or null when the user has never been asked. */
  public readonly cookiePreferences = this.cookiePreferencesState.asReadonly();

  /** Whether someone is signed in, as a signal, so the banner can appear without a reload. */
  private readonly signedInState = signal(false);

  public readonly isSignedIn = this.signedInState.asReadonly();

  /**
   * Whether a `/consents/me` read has settled for the current user — succeeded *or* failed.
   *
   * Settled, not succeeded: asking again is the safe way to be wrong, while never settling would
   * leave a user with no stored choice unasked for as long as the backend is down.
   */
  private readonly settledState = signal(false);

  public readonly isConsentResolved = this.settledState.asReadonly();

  /**
   * True while the banner still needs to be shown. Three states, not two: nothing on file anywhere
   * (ask), a choice on file (do not ask), and a choice that may be on file but has not arrived yet
   * (do not ask *yet*).
   *
   * That third case is what `settledState` is for. A choice made on another device lives on the
   * account, and `syncPurposesFromServer` adopts it when `/consents/me` lands; keying off local
   * storage alone flashes the banner for the length of that round trip, asking a question already
   * answered. A stored local choice short-circuits all of it, being read in the field initialiser.
   *
   * Gated on being signed in, because nothing non-essential runs before that: `TrackingLoaderService`
   * injects GA only on an explicit grant, so the unauthenticated app is already at `DENY_ALL`.
   * Asking anonymously would take a decision from someone with no permanent way back to it — every
   * entry point to the preferences is behind sign-in. The trade is that events on the public pages
   * never reach GA for a first-time visitor.
   */
  public readonly needsCookieDecision = computed(
    () => this.signedInState() && this.settledState() && this.cookiePreferencesState() === null
  );

  constructor() {
    // Applied before anything else runs, so a returning visitor's refusal is honoured on the first
    // paint rather than after a round trip.
    this.tracking.apply(this.cookiePreferencesState() ?? DENY_ALL);
    // Subscribed rather than sampled once: the banner has to appear on the next sign-in and go on
    // sign-out without a page load. This is also the only path that reaches the standalone pages
    // outside the private shell — nothing else there ever calls `load`.
    //
    // A root service outlives every consumer, so the teardown only fires when the injector itself
    // goes — which is what makes the subscription's lifetime stated rather than assumed, and what
    // ends it in a test that destroys the TestBed.
    this.auth
      .getUser()
      .pipe(takeUntilDestroyed())
      .subscribe(user => {
        this.signedInState.set(!!user);
        void this.whenResolved();
      });
  }

  /**
   * Resolve consent for whoever is signed in, then return. This is what the route guard awaits,
   * since the guard itself must not fetch and only reads the settled signals.
   *
   * Reading the auth state here rather than from a stored flag closes an ordering race: the caller
   * gets the current user whether or not this service was constructed before Firebase restored the
   * session.
   */
  public async whenResolved(): Promise<void> {
    const user = await firstValueFrom(this.auth.getUser());
    if (!user) {
      if (this.loadedForUid !== null) this.reset();
      return;
    }
    if (user.uid !== this.loadedForUid) {
      this.reset();
      this.loadedForUid = user.uid;
    }
    await this.load();
  }

  /**
   * Load the signed-in user's consent state. Idempotent unless `force` is set, and failures are
   * swallowed on purpose — see the class docstring on failing open.
   *
   * **Only a read that reached the backend marks the state loaded**, so one blip does not leave the
   * session ungated client-side for its whole lifetime, meeting the user at the cooler instead of
   * at the acceptance screen on their next navigation.
   *
   * `force` skips the failure backoff: a caller passing it is reacting to the server having just
   * contradicted this state, and needs the answer now.
   *
   * Concurrent callers share the read that is already in flight, so the several that fire at shell
   * mount cost one request rather than one each.
   */
  public async load(force: boolean = false): Promise<void> {
    if (this.loaded && !force) return;
    if (!force && this.withinFailureBackoff()) return;
    // `loaded` is only set after the read returns, so without this the three callers that reach
    // here within the same tick at shell mount — `AcceptLegalGateService.start`, its `NavigationEnd`
    // subscription, and the editorial notice — would each start their own `GET /consents/me`.
    // A forced caller still gets its own request: it is reacting to the server having contradicted
    // this state, and a read that began before that contradiction is exactly the stale answer.
    if (this.inFlight && !force) return this.inFlight;
    const read = this.read();
    this.inFlight = read;
    try {
      await read;
    } finally {
      // Only if it is still ours: a forced read started meanwhile owns the field.
      if (this.inFlight === read) this.inFlight = null;
    }
  }

  /**
   * The single `/consents/me` round trip `load` wraps.
   *
   * The uid is captured before the request and re-checked after it, and a response that no longer
   * matches is dropped whole. `reset()` can stop a later caller joining a read, but it cannot
   * cancel one already in flight — so without this, signing out as one user and in as another
   * inside a single round trip lets the first response land on the second user: their
   * `pendingDocuments` would be someone else's, and `syncPurposesFromServer` would adopt someone
   * else's cookie decision into this browser, the account-level state being the only place it
   * could have come from.
   */
  private async read(): Promise<void> {
    const forUid = this.loadedForUid;
    try {
      const response = await this.http.getConsents();
      if (forUid !== this.loadedForUid) return;
      if (response?.successful && response.data) {
        this.state.set(response.data);
        this.syncPurposesFromServer(response.data);
      }
      this.loaded = true;
      this.lastLoadFailureAt = null;
    } catch (error) {
      if (forUid !== this.loadedForUid) return;
      console.error('Could not load consent state; continuing without gating.', error);
      this.lastLoadFailureAt = Date.now();
    } finally {
      // Settled either way: not "loaded", but an answer to whether this browser has waited long
      // enough to ask about cookies. Skipped for a superseded read, which would otherwise let the
      // previous user's request answer that question for the current one.
      if (forUid === this.loadedForUid) this.settledState.set(true);
    }
  }

  /** Whether a read failed recently enough that another one should be skipped. */
  private withinFailureBackoff(): boolean {
    if (this.lastLoadFailureAt === null) return false;
    return Date.now() - this.lastLoadFailureAt < ConsentService.RETRY_AFTER_FAILURE_MS;
  }

  /** Forget everything cached for the previous user. Called on logout and on a user change. */
  public reset(): void {
    this.state.set(null);
    this.loaded = false;
    this.loadedForUid = null;
    // Whatever is in flight belongs to the previous user, so a caller after this must not join it.
    // Clearing the field does not cancel that request; `read` drops its own result by comparing the
    // uid it was issued for against `loadedForUid`.
    this.inFlight = null;
    // A new user's record has not been read yet, so the banner waits again rather than asking on
    // the previous user's behalf.
    this.settledState.set(false);
    // The backoff belongs to the previous user's failed read, not to this one: a new sign-in must
    // not inherit a window that would skip its first load.
    this.lastLoadFailureAt = null;
  }

  /**
   * Record acceptance of the given document versions.
   *
   * Each acceptance names the version that was actually rendered; the API rejects a stale one so a
   * document published mid-session cannot be accepted blind.
   *
   * The accepted documents leave `pendingDocuments` before the request is sent, and are restored if
   * it fails — otherwise a failed acceptance reads as recorded and nothing brings the gate back.
   * The API is the enforcement point either way, refusing until it has the acceptance on file.
   */
  public async acceptDocuments(
    acceptances: DocumentAcceptance[],
    channel: ConsentChannel = ConsentChannel.REACCEPTANCE_GATE
  ): Promise<void> {
    const previousState = this.state();
    const optimisticState = this.clearPending(
      acceptances.map(acceptance => acceptance.documentType)
    );
    try {
      const response = await this.http.acceptConsentDocuments(acceptances, channel);
      if (!response?.successful) {
        throw new Error(response?.message ?? 'Consent could not be recorded.');
      }
      if (response.data) {
        this.state.set(response.data);
        this.loaded = true;
      }
    } catch (error) {
      // Only when nothing else has written in the meantime: a reload that landed during the round
      // trip read the server, while this snapshot only predates it.
      if (this.state() === optimisticState) this.state.set(previousState);
      throw error;
    }
  }

  /** Drop the given documents from what is owed, and return the state that was set. */
  private clearPending(documentTypes: LegalDocumentType[]): ConsentState | null {
    const state = this.state();
    if (!state) return null;
    const cleared: ConsentState = {
      ...state,
      pendingDocuments: state.pendingDocuments.filter(
        document => !documentTypes.includes(document.documentType)
      ),
    };
    this.state.set(cleared);
    return cleared;
  }

  /**
   * Persist the cookie choice locally, apply it to the tags, and record it against the account.
   *
   * Local first: the browser is what actually has to honour the choice, and an anonymous visitor
   * has no account to record against. The server call is best-effort evidence on top.
   */
  public async setCookiePreferences(
    preferences: CookiePreferences,
    channel: ConsentChannel = ConsentChannel.COOKIE_BANNER
  ): Promise<void> {
    this.storePreferences(preferences);
    this.cookiePreferencesState.set({ ...preferences });
    this.tracking.apply(preferences);
    await this.recordPurposes(
      {
        [ConsentPurpose.COOKIES_ANALYTICS]: preferences.analytics,
        [ConsentPurpose.COOKIES_MARKETING]: preferences.marketing,
      },
      channel
    );
  }

  public acceptAllCookies(channel?: ConsentChannel): Promise<void> {
    return this.setCookiePreferences({ ...ALLOW_ALL }, channel);
  }

  public rejectAllCookies(channel?: ConsentChannel): Promise<void> {
    return this.setCookiePreferences({ ...DENY_ALL }, channel);
  }

  /** Grant or withdraw a single purpose, e.g. the marketing-email opt-in. */
  public async setPurpose(
    purpose: ConsentPurpose,
    granted: boolean,
    channel: ConsentChannel = ConsentChannel.SETTINGS
  ): Promise<void> {
    await this.recordPurposes({ [purpose]: granted }, channel);
  }

  /** Whether a purpose has been decided at all — "never asked" is not the same as "said no". */
  public hasDecided(purpose: ConsentPurpose): boolean {
    return this.state()?.purposes?.[purpose] !== undefined;
  }

  /**
   * Document types whose current version the user has not seen, but is not required to accept.
   *
   * The editorial half of the re-consent duty: DPP section XVII commits REITE to *notifying*
   * updates, section XIII to *express re-acceptance* only for significant ones. Derived rather than
   * served, because the API omits non-blocking updates from `pendingDocuments` by design.
   */
  public async getEditorialUpdates(lang: string): Promise<EditorialUpdate[]> {
    const state = this.state();
    if (!state) return [];
    const pending = new Set(this.pendingDocuments().map(document => document.documentType));
    try {
      const response = await this.http.getLegalDocumentRequirements(lang);
      if (!response?.successful || !response.data) return [];
      return response.data
        .filter(requirement => {
          if (!requirement.currentVersion) return false;
          if (pending.has(requirement.documentType)) return false;
          const accepted = state.documents?.[requirement.documentType];
          // Never accepted at all is not an editorial update — that case is blocking, and it is
          // already covered by `pendingDocuments`.
          return !!accepted && accepted.version !== requirement.currentVersion;
        })
        .map(requirement => ({
          documentType: requirement.documentType,
          version: requirement.currentVersion as string,
        }));
    } catch (error) {
      console.error('Could not check for legal document updates.', error);
      return [];
    }
  }

  /**
   * The account's consent history, newest first, as the append-only log holds it.
   *
   * The one read here that does **not** fail open. Everywhere else an unreachable backend resolves
   * to "nothing pending", because leaving a user ungated is the safe way to be wrong. An empty
   * history is not a safe way to be wrong: it answers "you have agreed to nothing", which is a
   * false statement about the user's own record and the exact question this data answers. So a
   * failure is raised and the caller has to show it as a failure.
   */
  public async getEvents(): Promise<ConsentEvent[]> {
    const response = await this.http.getConsentEvents();
    if (!response?.successful || !response.data) {
      throw new Error(response?.message ?? 'Consent history could not be read.');
    }
    return response.data;
  }

  /** Remember that this browser has been told about a given version, so it is shown once. */
  public markUpdateNoticeSeen(documentType: LegalDocumentType, version: string): void {
    this.localStorage.setItem(`${UPDATE_NOTICE_KEY_PREFIX}${documentType}`, version, true);
  }

  public hasSeenUpdateNotice(documentType: LegalDocumentType, version: string): boolean {
    try {
      return this.localStorage.getItem(`${UPDATE_NOTICE_KEY_PREFIX}${documentType}`) === version;
    } catch {
      return false;
    }
  }

  public isGranted(purpose: ConsentPurpose): boolean {
    return this.state()?.purposes?.[purpose]?.granted ?? false;
  }

  private async recordPurposes(
    purposes: Partial<Record<ConsentPurpose, boolean>>,
    channel: ConsentChannel
  ): Promise<void> {
    try {
      const response = await this.http.setConsentPurposes(purposes, channel);
      if (response?.successful && response.data) {
        this.state.set(response.data);
        this.loaded = true;
      }
    } catch (error) {
      // An anonymous visitor has no account to record against, and a signed-in user's local choice
      // is already applied. Losing the server-side copy is not a reason to fail the interaction.
      console.error('Could not record consent purposes.', error);
    }
  }

  /**
   * Adopt the account's stored cookie decision when this browser has none, so signing in on a new
   * device honours the choice made elsewhere. A choice already made *in this browser* always wins:
   * it is the one made for the device actually storing the cookies.
   */
  private syncPurposesFromServer(state: ConsentState): void {
    if (this.cookiePreferencesState() !== null) return;
    const analytics = state.purposes?.[ConsentPurpose.COOKIES_ANALYTICS];
    const marketing = state.purposes?.[ConsentPurpose.COOKIES_MARKETING];
    if (analytics === undefined && marketing === undefined) return;
    const preferences: CookiePreferences = {
      analytics: analytics?.granted ?? false,
      marketing: marketing?.granted ?? false,
    };
    this.storePreferences(preferences);
    this.cookiePreferencesState.set(preferences);
    this.tracking.apply(preferences);
  }

  private readStoredPreferences(): CookiePreferences | null {
    try {
      const stored: StoredCookiePreferences | null = this.localStorage.getItem(
        COOKIE_PREFERENCES_KEY
      );
      if (!stored || stored.version !== COOKIE_PREFERENCES_VERSION) return null;
      return { analytics: !!stored.analytics, marketing: !!stored.marketing };
    } catch {
      return null;
    }
  }

  private storePreferences(preferences: CookiePreferences): void {
    const stored: StoredCookiePreferences = {
      ...preferences,
      version: COOKIE_PREFERENCES_VERSION,
      at: Date.now(),
    };
    this.localStorage.setItem(COOKIE_PREFERENCES_KEY, stored, true);
  }
}
